import { z } from "zod";
import { IsoDateTimeSchema } from "./primitives.js";

/**
 * Terminal states a confirmation can resolve into. Shared by every surface
 * (hub-chat SSE events, decision acks, the resolved-list projection) so all of
 * them render the same truth:
 *
 *   - `approved` / `denied` — a human decided.
 *   - `expired`   — unanswered past the gateway's expiry window; the parked
 *                   turn resumed as denied with an expiry reason.
 *   - `cancelled` — the session was aborted (/stop) or the agent was deleted
 *                   before anyone decided; the parked turn never resumed.
 */
export const ConfirmationTerminalStateSchema = z.enum([
  "approved",
  "denied",
  "expired",
  "cancelled",
]);
export type ConfirmationTerminalState = z.infer<typeof ConfirmationTerminalStateSchema>;

/**
 * The prior outcome carried when a decision arrives for an already-resolved
 * confirmation. `resolvedBy` is the Hub-canonical user id of the decider,
 * absent for expiry/cancellation terminals nobody clicked.
 */
export const ConfirmationPriorResolutionSchema = z
  .object({
    state: ConfirmationTerminalStateSchema,
    resolvedBy: z.string().min(1).max(256).optional(),
    resolvedAt: IsoDateTimeSchema,
  })
  .strict();
export type ConfirmationPriorResolution = z.infer<typeof ConfirmationPriorResolutionSchema>;
